import { Frame } from 'data/Frame';
import * as React from 'react';
import { PointData } from '../data/FlyerData';
import { Resolution } from '../data/Resolution';
import { getCanvasScale, getStoreState } from '../state/Store';
import { generateClassName, rotatePoint } from '../utils/index';

enum SnapDirection {
  horizontal,
  vertical
}

interface Snap {
  direction: SnapDirection;
  value: number;
}

// enum Snap {
//   top = 'top',
//   right = 'right',
//   bottom = 'bottom',
//   left = 'left',
//   center = 'center',
//   middle = 'middle'
// }

const SNAP_DISTANCE = 5;

interface Props {
  reactangle: PIXI.Rectangle;
  rotation: number;
  canRotate: Boolean;
  maintainAspectRatio: boolean;
  stageResolution: Resolution;
  onChange: (newRect: PIXI.Rectangle) => void;
  onRotate: (newAngle: number) => void;
  onClick: (point: PointData) => void;
  canEditContent: boolean;
  onStartedEditingContent: () => void;
  initialMousePosition: PointData | null;
  selectedFrame: Frame;
  // onStoppedEditingContent: () => void;
  // isEditingContent: boolean;
}

interface State {
  snaps: Snap[];
}

export class BoundingBox extends React.Component<Props, State> {
  el = React.createRef<HTMLDivElement>();

  topLeftCorner = React.createRef<HTMLDivElement>();
  topRightCorner = React.createRef<HTMLDivElement>();
  bottomLeftCorner = React.createRef<HTMLDivElement>();
  bottomRightCorner = React.createRef<HTMLDivElement>();

  rotationPivotTop = React.createRef<HTMLDivElement>();
  rotationPivotRight = React.createRef<HTMLDivElement>();
  rotationPivotBottom = React.createRef<HTMLDivElement>();
  rotationPivotLeft = React.createRef<HTMLDivElement>();

  box = React.createRef<HTMLDivElement>();

  draggedEl: HTMLDivElement;
  lastMousePosition: PointData = { x: 0, y: 0 };
  lastBox: PIXI.Rectangle;

  initialProportions = 1;

  possibleLeftSnaps: number[];
  possibleTopSnaps: number[];
  possibleRightSnaps: number[];
  possibleBottomSnaps: number[];
  possibleCenterHorizontalSnaps: number[];
  possibleCenterVerticalSnaps: number[];

  state: State = {
    snaps: []
  };

  componentDidMount() {
    window.addEventListener('resize', this.didResizeWindow);
    const { initialMousePosition } = this.props;
    if (initialMousePosition && this.box.current) {
      // start dragging
      this.draggedEl = this.box.current;
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
      this.box.current.style.cursor = 'grabbing';
      this.lastMousePosition = this.getLocalPosition(initialMousePosition);
      this.lastBox = this.props.reactangle.clone();
    }
    this.generateSnaps();
  }

  generateSnaps() {
    const { selectedFrame, stageResolution } = this.props;
    // get all frames other than the selected one
    const otherFrames: Frame[] = [];
    getStoreState().flyer.layers.forEach(l => {
      l.frames.forEach(f => {
        if (f !== selectedFrame) {
          otherFrames[otherFrames.length] = f;
        }
      });
    });

    let set: Set<number>;
    // left
    set = new Set<number>();
    set.add(0);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.x);
    });
    this.possibleLeftSnaps = Array.from(set);
    // top
    set = new Set<number>();
    set.add(0);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.y);
    });
    this.possibleTopSnaps = Array.from(set);
    // right
    set = new Set<number>();
    set.add(stageResolution.width);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.right);
    });
    this.possibleRightSnaps = Array.from(set);
    // bottom
    set = new Set<number>();
    set.add(stageResolution.height);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.bottom);
    });
    this.possibleBottomSnaps = Array.from(set);
    // center horizontal
    set = new Set<number>();
    set.add(stageResolution.width / 2);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.x + f.boundingBox.width / 2);
    });
    this.possibleCenterHorizontalSnaps = Array.from(set);
    // center vertical
    set = new Set<number>();
    set.add(stageResolution.height / 2);
    otherFrames.forEach(f => {
      set.add(f.boundingBox.y + f.boundingBox.height / 2);
    });
    this.possibleCenterVerticalSnaps = Array.from(set);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.didResizeWindow);
  }

  didResizeWindow = () => {
    this.forceUpdate();
  };

  onMouseDown = (event: React.MouseEvent) => {
    // if (this.props.isEditingContent) {
    //   return;
    // }
    const target = event.target as HTMLDivElement;
    if (
      [
        this.topLeftCorner.current,
        this.topRightCorner.current,
        this.bottomLeftCorner.current,
        this.bottomRightCorner.current,
        this.rotationPivotTop.current,
        this.rotationPivotRight.current,
        this.rotationPivotBottom.current,
        this.rotationPivotLeft.current,
        this.box.current
      ].includes(target)
    ) {
      this.draggedEl = event.target as HTMLDivElement;
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('mouseup', this.onMouseUp);
      if (this.draggedEl === this.box.current) {
        this.box.current.style.cursor = 'grabbing';
      }
      this.lastMousePosition = this.getLocalPosition({
        x: event.clientX,
        y: event.clientY
      });
      this.lastBox = this.props.reactangle.clone();
    }
  };

  getLocalPosition = (p: PointData): PointData => {
    const parentRect = this.el.current!.getBoundingClientRect();
    const scale = getCanvasScale();
    const x = (p.x - parentRect.x) * (1 / scale);
    const y = (p.y - parentRect.y) * (1 / scale);
    return { x, y };
  };

  onMouseMove = (event: MouseEvent) => {
    let p: PointData = this.getLocalPosition({
      x: event.clientX,
      y: event.clientY
    });
    const prevRect = this.lastBox;
    let newRect: PIXI.Rectangle | undefined = undefined;
    const prevProportions = prevRect.width / prevRect.height;
    const forceAspectRatio = (w: number, h: number): [number, number] => {
      // TODO: this could be smarter
      // const newProportions = w / h;
      // if (newProportions === prevProportions) {
      //   return [w, h];
      // }
      // const newW
      const newHeight = w / prevProportions;
      return [w, newHeight];
    };
    let { x, y, width, height } = prevRect;
    const centerX = x + width / 2;
    const centerY = y + height / 2;
    const { rotation } = this.props;
    const minSize = 10;

    const rotate = (delta: number) => {
      let degrees =
        (Math.atan2(p.y - centerY, p.x - centerX) * 180) / Math.PI + delta;
      if (event.shiftKey) {
        // round
        degrees = Math.round(degrees / 45) * 45;
      }
      while (degrees < -180) {
        degrees += 360;
      }
      while (degrees > 180) {
        degrees -= 360;
      }
      this.props.onRotate(degrees);
    };

    switch (this.draggedEl) {
      case this.rotationPivotTop.current:
        rotate(90);
        return;
      case this.rotationPivotRight.current:
        rotate(0);
        return;
      case this.rotationPivotBottom.current:
        rotate(-90);
        return;
      case this.rotationPivotLeft.current:
        rotate(-180);
        return;
      case this.topLeftCorner.current:
        p = rotatePoint(p, { x: centerX, y: centerY }, -rotation);
        p.x = Math.min(p.x, prevRect.right - minSize);
        p.y = Math.min(p.y, prevRect.bottom - minSize);
        width = prevRect.right - p.x;
        height = prevRect.bottom - p.y;
        if (this.props.maintainAspectRatio) {
          const values = forceAspectRatio(width, height);
          width = values[0];
          height = values[1];
        }
        newRect = new PIXI.Rectangle(
          prevRect.right - width,
          prevRect.bottom - height,
          width,
          height
        );
        break;
      case this.topRightCorner.current:
        p = rotatePoint(p, { x: centerX, y: centerY }, -rotation);
        p.x = Math.max(p.x, prevRect.left + minSize);
        p.y = Math.min(p.y, prevRect.bottom - minSize);
        width = p.x - prevRect.x;
        height = prevRect.bottom - p.y;
        if (this.props.maintainAspectRatio) {
          const values = forceAspectRatio(width, height);
          width = values[0];
          height = values[1];
        }
        newRect = new PIXI.Rectangle(
          prevRect.x,
          prevRect.bottom - height,
          width,
          height
        );
        break;
      case this.bottomLeftCorner.current:
        p = rotatePoint(p, { x: centerX, y: centerY }, -rotation);
        p.x = Math.min(p.x, prevRect.right - minSize);
        p.y = Math.max(p.y, prevRect.top + minSize);
        width = prevRect.right - p.x;
        height = p.y - prevRect.y;
        if (this.props.maintainAspectRatio) {
          const values = forceAspectRatio(width, height);
          width = values[0];
          height = values[1];
        }
        newRect = new PIXI.Rectangle(
          prevRect.right - width,
          prevRect.y,
          width,
          height
        );
        break;
      case this.bottomRightCorner.current:
        p = rotatePoint(p, { x: centerX, y: centerY }, -rotation);
        p.x = Math.max(p.x, prevRect.left + minSize);
        p.y = Math.max(p.y, prevRect.top + minSize);
        width = p.x - prevRect.x;
        height = p.y - prevRect.y;
        if (this.props.maintainAspectRatio) {
          const values = forceAspectRatio(width, height);
          width = values[0];
          height = values[1];
        }
        newRect = new PIXI.Rectangle(prevRect.x, prevRect.y, width, height);
        break;
      case this.box.current:
        const dx = p.x - this.lastMousePosition.x;
        const dy = p.y - this.lastMousePosition.y;
        newRect = new PIXI.Rectangle(
          prevRect.x + dx,
          prevRect.y + dy,
          prevRect.width,
          prevRect.height
        );
        break;
      default:
        break;
    }
    if (!newRect) {
      return;
    }
    let snaps: Snap[] = [];
    let horizontalSnap: Snap | undefined;
    let verticalSnap: Snap | undefined;
    // left
    for (const s of this.possibleLeftSnaps) {
      if (Math.abs(s - newRect.x) <= SNAP_DISTANCE) {
        horizontalSnap = {
          direction: SnapDirection.horizontal,
          value: s
        };
        newRect.x = s;
        break;
      }
    }
    // center x
    if (!horizontalSnap) {
      for (const s of this.possibleCenterHorizontalSnaps) {
        if (Math.abs(s - (newRect.x + newRect.width / 2)) <= SNAP_DISTANCE) {
          horizontalSnap = {
            direction: SnapDirection.horizontal,
            value: s
          };
          newRect.x = s - newRect.width / 2;
          break;
        }
      }
    }
    // right
    if (!horizontalSnap) {
      for (const s of this.possibleRightSnaps) {
        if (Math.abs(s - newRect.right) <= SNAP_DISTANCE) {
          horizontalSnap = {
            direction: SnapDirection.horizontal,
            value: s
          };
          newRect.x = s - newRect.width;
          break;
        }
      }
    }
    // top
    for (const s of this.possibleTopSnaps) {
      if (Math.abs(s - newRect.y) <= SNAP_DISTANCE) {
        verticalSnap = {
          direction: SnapDirection.vertical,
          value: s
        };
        newRect.y = s;
        break;
      }
    }
    // center y
    if (!verticalSnap) {
      for (const s of this.possibleCenterVerticalSnaps) {
        if (Math.abs(s - (newRect.y + newRect.height / 2)) <= SNAP_DISTANCE) {
          verticalSnap = {
            direction: SnapDirection.vertical,
            value: s
          };
          newRect.y = s - newRect.height / 2;
          break;
        }
      }
    }
    // bottom
    if (!verticalSnap) {
      for (const s of this.possibleBottomSnaps) {
        if (Math.abs(s - newRect.bottom) <= SNAP_DISTANCE) {
          verticalSnap = {
            direction: SnapDirection.vertical,
            value: s
          };
          newRect.y = s - newRect.height;
          break;
        }
      }
    }
    if (horizontalSnap) {
      snaps.push(horizontalSnap);
    }
    if (verticalSnap) {
      snaps.push(verticalSnap);
    }

    this.setState({ snaps });
    this.props.onChange(newRect);
  };

  onMouseUp = (event: MouseEvent) => {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    if (this.box.current) {
      this.box.current.style.cursor = 'grab';
      // this.box.current.style.cursor = this.props.isEditingContent
      //   ? 'text'
      //   : 'grab';
    }
    if (this.state.snaps.length) {
      this.setState({ snaps: [] });
    }
  };

  didClickBox = (event: React.MouseEvent<HTMLDivElement>) => {
    // if (!this.props.isEditingContent) {
    //   return;
    // }
    this.props.onClick(
      this.getLocalPosition({
        x: event.clientX,
        y: event.clientY
      })
    );
  };

  didDoubleClickBox = () => {
    // console.log('Double Clicked box');
    this.props.onStartedEditingContent();
  };

  renderSnap(scale: number): JSX.Element | null {
    const { snaps } = this.state;
    return (
      <>
        {snaps.map(s => {
          switch (s.direction) {
            case SnapDirection.horizontal:
              return (
                <div
                  className="dotted-line dotted-line--vertical"
                  key={s.direction}
                  style={{
                    left: s.value * scale,
                    top: -10000,
                    width: 0,
                    height: 20000
                  }}
                />
              );
            case SnapDirection.vertical:
              return (
                <div
                  className="dotted-line dotted-line--horizontal"
                  key={s.direction}
                  style={{
                    left: -10000,
                    top: s.value * scale,
                    width: 20000,
                    height: 0
                  }}
                />
              );
            default:
              return null;
          }
        })}
      </>
    );
  }

  render() {
    const { canRotate } = this.props;
    const r = this.props.reactangle;
    const scale = getCanvasScale();
    const width = r.width * scale;
    const height = r.height * scale;
    const pad = /*this.props.isEditingContent ? 0 :*/ 5;
    const pad2 = pad * 2;

    const cx = (r.left + r.width / 2) * scale;
    const cy = (r.top + r.height / 2) * scale;
    const wh = width / 2;
    const hh = height / 2;
    const cornerPivotDistance = 20;
    const rotatePointTransform = `translate(-6px, -6px) rotate(45deg)`;
    const rotatePointCursor = `url(/media/cursor-rotate.svg) 12 12, crosshair`;
    return (
      <div
        className={generateClassName('bounding-box', {
          // editing: this.props.isEditingContent
        })}
        onMouseDown={this.onMouseDown}
        ref={this.el}
      >
        {this.renderSnap(scale)}
        <div
          className="sprite"
          style={{
            position: 'absolute',
            left: cx,
            top: cy,
            transform: `rotate(${this.props.rotation}deg)`
          }}
        >
          <div
            className="line"
            style={{
              top: -hh,
              left: -wh + pad,
              width: width - pad2,
              height: 0
            }}
          />
          <div
            className="line"
            style={{
              top: -hh + pad,
              left: wh,
              width: 0,
              height: height - pad2
            }}
          />
          <div
            className="line"
            style={{
              top: hh,
              left: -wh + pad,
              width: width - pad2,
              height: 0
            }}
          />
          <div
            className="line"
            style={{
              top: -hh + pad,
              left: -wh,
              width: 0,
              height: height - pad2
            }}
          />
          {canRotate && (
            <>
              <div
                className="line"
                style={{
                  top: -(hh + cornerPivotDistance),
                  left: 0,
                  width: 0,
                  height: cornerPivotDistance
                }}
              />
              <div
                className="line"
                style={{
                  top: 0,
                  left: wh,
                  width: cornerPivotDistance,
                  height: 0
                }}
              />
              <div
                className="line"
                style={{
                  top: hh,
                  left: 0,
                  width: 0,
                  height: cornerPivotDistance
                }}
              />
              <div
                className="line"
                style={{
                  top: 0,
                  left: -(wh + cornerPivotDistance),
                  width: cornerPivotDistance,
                  height: 0
                }}
              />
            </>
          )}
          <div
            className="box"
            style={{
              left: -wh,
              top: -hh,
              width,
              height,
              cursor: /*this.props.isEditingContent ? 'text' :*/ 'grab'
            }}
            ref={this.box}
            onClick={
              /*this.props.canEditContent && this.props.isEditingContent
              ? this.didClickBox
              :*/ undefined
            }
            onDoubleClick={
              this.props.canEditContent /* && !this.props.isEditingContent*/
                ? this.didDoubleClickBox
                : undefined
            }
          />
          {/* {!this.props.isEditingContent && ( */}
          <>
            <div
              className="draggable-corner"
              style={{
                left: -wh,
                top: -hh,
                cursor: 'nwse-resize'
              }}
              ref={this.topLeftCorner}
            />
            <div
              className="draggable-corner"
              style={{
                left: wh,
                top: -hh,
                cursor: 'nesw-resize'
              }}
              ref={this.topRightCorner}
            />
            <div
              className="draggable-corner"
              style={{
                left: -wh,
                top: hh,
                cursor: 'nesw-resize'
              }}
              ref={this.bottomLeftCorner}
            />
            <div
              className="draggable-corner"
              style={{
                left: wh,
                top: hh,
                cursor: 'nwse-resize'
              }}
              ref={this.bottomRightCorner}
            />
            {canRotate && (
              <>
                <div
                  className="draggable-corner"
                  style={{
                    left: 0,
                    top: -(hh + cornerPivotDistance),
                    transform: rotatePointTransform,
                    cursor: rotatePointCursor
                  }}
                  ref={this.rotationPivotTop}
                />
                <div
                  className="draggable-corner"
                  style={{
                    left: wh + cornerPivotDistance,
                    top: 0,
                    transform: rotatePointTransform,
                    cursor: rotatePointCursor
                  }}
                  ref={this.rotationPivotRight}
                />
                <div
                  className="draggable-corner"
                  style={{
                    left: 0,
                    top: hh + cornerPivotDistance,
                    transform: rotatePointTransform,
                    cursor: rotatePointCursor
                  }}
                  ref={this.rotationPivotBottom}
                />
                <div
                  className="draggable-corner"
                  style={{
                    left: -(wh + cornerPivotDistance),
                    top: 0,
                    transform: rotatePointTransform,
                    cursor: rotatePointCursor
                  }}
                  ref={this.rotationPivotLeft}
                />
              </>
            )}
          </>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/showcase/BoundingBox.tsx